import { Link, useLocation, useParams } from 'react-router-dom'
import { TaskDetailView } from '../components/TaskViews'
import { useTasks } from '../hooks/useTasks'
import type { EmailTask } from '../types'

type ListNavState = { listSearch?: string }

function todosHref(task: EmailTask, listSearch: string | undefined): string {
  if (listSearch != null) return listSearch ? `/?${listSearch}` : '/'
  // Fallback when we didn't arrive from the list (deep link / refresh without storage).
  if (task.status === 'done' || task.status === 'dismissed') {
    return `/?status=${task.status}`
  }
  return '/'
}

function readListSearch(state: unknown): string | undefined {
  const fromState = (state as ListNavState | null)?.listSearch
  if (typeof fromState === 'string') return fromState
  const stored = sessionStorage.getItem('tasks.listSearch')
  return stored ?? undefined
}

export function TaskDetailPage() {
  const { taskId = '' } = useParams()
  const location = useLocation()
  const { optimistic, loading, error, getTask } = useTasks()
  const task = getTask(taskId)
  const children = optimistic
    .filter((t) => t.parent_id === taskId)
    .sort((a, b) => a.title.localeCompare(b.title))
  const listSearch = readListSearch(location.state)
  const crumbState: ListNavState = { listSearch }

  if (loading && !task) {
    return (
      <div className="shell">
        <p className="empty">Loading…</p>
      </div>
    )
  }

  if (!task) {
    return (
      <div className="shell">
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/" className="crumb">
            ← Todos
          </Link>
        </nav>
        <p className="empty">{error ? error : 'Todo not found.'}</p>
      </div>
    )
  }

  const parent = task.parent_id ? getTask(task.parent_id) : undefined
  const grand = parent?.parent_id ? getTask(parent.parent_id) : undefined
  const backTo = todosHref(task, listSearch)

  return (
    <div className="shell">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to={backTo} className="crumb">
          ← Todos
        </Link>
        {grand ? (
          <>
            <span className="crumb-sep" aria-hidden>
              ←
            </span>
            <Link
              to={`/tasks/${grand.id}`}
              state={crumbState}
              className="crumb"
              title={grand.title}
            >
              {grand.title}
            </Link>
          </>
        ) : null}
        {parent ? (
          <>
            <span className="crumb-sep" aria-hidden>
              ←
            </span>
            <Link
              to={`/tasks/${parent.id}`}
              state={crumbState}
              className="crumb"
              title={parent.title}
            >
              {parent.title}
            </Link>
          </>
        ) : null}
        <span className="crumb-sep" aria-hidden>
          ←
        </span>
        <span className="crumb current" title={task.title}>
          {task.title}
        </span>
      </nav>
      <TaskDetailView task={task} childrenTasks={children} />
    </div>
  )
}
